// src/components/reports/SalesSummaryFilters.jsx
import React, { useState, useEffect } from 'react'
import { Calendar, Search, X, ChevronDown } from 'lucide-react'
import BranchService from '../../services/branchService'
import AuthService from '../../services/authService'

const SalesSummaryFilters = ({ filters = {}, onFiltersChange, onApply, onClear }) => {
  const [branches, setBranches] = useState([])
  const [loadingBranches, setLoadingBranches] = useState(true)
  
  // Get user role and branch
  const user = AuthService.getCurrentUser()
  const isManager = user?.role === 'manager'
  const userBranchId = user?.selectedBranch?._id || user?.branchId || user?.branch?._id
  
  const [localFilters, setLocalFilters] = useState({
    fromDate: '',
    toDate: '',
    orderType: '',
    paymentMethod: '',
    customerSearch: '',
    branch: isManager ? (userBranchId || '') : '',
    ...filters
  })

  // Load branches from API
  useEffect(() => {
    const loadBranches = async () => {
      try {
        setLoadingBranches(true)
        console.log('🔍 Sales Summary: Loading branches')
        
        const branchResponse = await BranchService.getAllBranches()
        console.log('✅ Sales Summary: Branches response:', branchResponse)
        
        let branchesData = []
        if (branchResponse && branchResponse.success) {
          if (branchResponse.data && branchResponse.data.branches && Array.isArray(branchResponse.data.branches)) {
            branchesData = branchResponse.data.branches
          } else if (Array.isArray(branchResponse.data)) {
            branchesData = branchResponse.data
          }
        }
        
        // Filter to only show outlet branches (exclude warehouses)
        const outletBranches = branchesData.filter(branch => branch.type === 'outlet' && branch.isActive)
        console.log('🏪 Sales Summary: Filtered to active outlets:', outletBranches.length, 'branches')
        setBranches(outletBranches)
        
      } catch (error) {
        console.error('❌ Sales Summary: Error loading branches:', error)
      } finally {
        setLoadingBranches(false)
      }
    }
    
    loadBranches()
  }, [])

  // Initialize manager's branch filter on mount
  useEffect(() => {
    if (isManager && userBranchId && typeof onFiltersChange === 'function') {
      const initialFilters = {
        ...filters,
        branch: userBranchId
      }
      console.log('🔧 Manager detected - Setting initial branch filter:', userBranchId)
      onFiltersChange(initialFilters)
    }
  }, [isManager, userBranchId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLocalFilters(prev => ({
      ...prev,
      ...filters
    }))
  }, [filters])

  const handleFilterChange = (key, value) => {
    const newFilters = {
      ...localFilters,
      [key]: value
    }
    setLocalFilters(newFilters)
    
    // Update parent state - BaseReportPage will auto-apply with debounce
    if (typeof onFiltersChange === 'function') {
      onFiltersChange(newFilters)
    }
  }

  const handleClearAll = () => {
    const clearedFilters = {
      fromDate: '',
      toDate: '',
      orderType: '',
      paymentMethod: '',
      customerSearch: '',
      branch: isManager ? (userBranchId || '') : ''
    }
    setLocalFilters(clearedFilters)
    
    if (typeof onFiltersChange === 'function') {
      onFiltersChange(clearedFilters)
    }
    if (typeof onClear === 'function') {
      onClear()
    }
  }

  const formatDateForInput = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toISOString().split('T')[0]
  }

  // Check if any filters are active (exclude manager's locked branch filter)
  const hasActiveFilters = localFilters.fromDate || localFilters.toDate || localFilters.orderType || localFilters.paymentMethod || localFilters.customerSearch || (!isManager && localFilters.branch)

  return (
    <div className="bg-gray-50 p-4 rounded-lg mb-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={handleClearAll}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-orange-600 bg-white border border-orange-300 rounded-lg hover:bg-orange-50 hover:text-orange-700 hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 transition-colors"
          >

            <span>Clear Filters</span>
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* Branch Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
          <div className="relative">
            {isManager ? (
              // Manager: Read-only branch display
              <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700 font-medium">
                {branches.find(b => b._id === userBranchId)?.name || 'My Branch'}
              </div>
            ) : (
              // Admin: Branch dropdown
              <>
                <select
                  value={localFilters.branch || ''}
                  onChange={(e) => handleFilterChange('branch', e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10 appearance-none"
                  disabled={loadingBranches}
                >
                  <option value="">All Branches</option>
                  {branches.map((branch) => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </>
            )}
          </div>
          {loadingBranches && !isManager && <p className="text-xs text-gray-500 mt-1">Loading branches...</p>}
        </div>

        {/* From Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
          <div className="relative">
            <input
              type="date"
              value={formatDateForInput(localFilters.fromDate)}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* To Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
          <div className="relative">
            <input
              type="date"
              value={formatDateForInput(localFilters.toDate)}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Order Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Order Type</label>
          <div className="relative">
            <select
              value={localFilters.orderType}
              onChange={(e) => handleFilterChange('orderType', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10 appearance-none"
            >
              <option value="">All Order Types</option>
              <option value="dine_in">Dine In</option>
              <option value="take_away">Takeaway</option>
              <option value="delivery">Delivery</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Payment Method */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
          <div className="relative">
            <select
              value={localFilters.paymentMethod}
              onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10 appearance-none"
            >
              <option value="">All Payment Methods</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="credit">Credit</option>
              <option value="redeem">Points Redeem</option>
              <option value="multiple">Multiple</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>
      
      {/* Customer Search Row */}
      {localFilters.customerSearch !== undefined && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Customer Search</label>
          <div className="relative">
            <input
              type="text"
              placeholder="Search customer..."
              value={localFilters.customerSearch}
              onChange={(e) => handleFilterChange('customerSearch', e.target.value)}
              className="w-full px-3 py-2 pl-10 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          </div>
        </div>
      )}
    </div>
  )
}

export default SalesSummaryFilters